/**
 * Google reCAPTCHA v3 client helper. Canonical SakthiInc pattern —
 * mirrors scribenza/budgeza/mathacad utils.
 *
 * Site key + enabled flag are fetched from {VITE_API_URL}/config/security
 * on first use. When disabled, executeRecaptcha() resolves to null so
 * callers can pass `recaptcha_token: null` — backend skips verify.
 *
 * Lazy + idempotent: first call to executeRecaptcha injects the Google
 * script; subsequent calls reuse the same global.
 */

let configPromise = null
let scriptPromise = null
let cachedConfig = null

async function loadConfig() {
  if (cachedConfig) return cachedConfig
  if (!configPromise) {
    const base = import.meta.env.VITE_API_URL || ''
    configPromise = fetch(`${base}/config/security`, {
      headers: { Accept: 'application/json' },
    })
      .then((r) => r.json())
      .then((j) => j?.recaptcha || { enabled: false, site_key: null })
      .catch(() => ({ enabled: false, site_key: null }))
  }
  cachedConfig = await configPromise
  return cachedConfig
}

function loadScript(siteKey) {
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve, reject) => {
    if (window.grecaptcha?.execute) return resolve()
    const s = document.createElement('script')
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`
    s.async = true
    s.defer = true
    s.onload = () => {
      window.grecaptcha?.ready
        ? window.grecaptcha.ready(resolve)
        : resolve()
    }
    s.onerror = () => reject(new Error('Failed to load reCAPTCHA'))
    document.head.appendChild(s)
  })
  return scriptPromise
}

export async function executeRecaptcha(action) {
  const cfg = await loadConfig()
  if (!cfg.enabled || !cfg.site_key) return null
  try {
    await loadScript(cfg.site_key)
    return await window.grecaptcha.execute(cfg.site_key, { action })
  } catch (e) {
    console.warn('[recaptcha] execute failed', e)
    return null
  }
}

export function resetRecaptchaConfig() {
  configPromise = null
  scriptPromise = null
  cachedConfig = null
}
